<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2019-2023 (original work) Open Assessment Technologies SA ;
    import { createEventDispatcher } from 'svelte';
    import { getActualKey } from '@oat-sa-private/ui-core';
    import TableHeadCell from './TableHeadCell.svelte';

    export let columns;
    export let rows;
    export let allSelected = false;
    export let someSelected = false;

    const dispatch = createEventDispatcher();

    /**
     * Dispatch sort event for specific column
     * @param {Object[]} column - column configuration object
     */
    function dispatchSort(column) {
        columns = columns.map(item => {
            if (item === column) {
                if (!item.sortOrder) {
                    item.sortOrder = item.lastSortOrder || 'asc';
                } else {
                    item.sortOrder = item.sortOrder === 'asc' ? 'desc' : 'asc';
                }
                item.lastSortOrder = item.sortOrder;
            } else {
                delete item.lastSortOrder;
                delete item.sortOrder;
            }
            return item;
        });

        dispatch('action', {
            type: 'sort',
            key: column.key,
            resource: column,
            options: {
                sortOrder: column.sortOrder
            }
        });
    }

    /**
     * Selection change handler
     *
     * @param {Number} columnIndex - index of selected column
     * @param {String} columnKey - key from configuration of selected column
     * @param {Boolean} checked - selection state
     */
    function onSelectChange(columnIndex, columnKey, checked) {
        rows = rows.map(row => {
            if (!row[columnIndex].disabled) {
                row[columnIndex].checked = checked;
            }

            return row;
        });

        dispatch('action', {
            type: 'selectAll',
            key: columnKey,
            options: {
                checked
            }
        });
    }

    /**
     * Create click handler for column
     *
     * @param {Object} column - column configuration object
     * @returns {Function|void} returns column click handler function if column is sortable
     * and undefined if it's not
     */
    function createColumnClickHandler(column) {
        return column.sortable ? () => dispatchSort(column) : void 0;
    }

    /**
     * Create keydown handler for column
     *
     * @param {Object} column - column configuration object
     * @returns {Function|void} returns column keydown handler function if column is sortable
     * and undefined if it's not
     */
    function createColumnKeydownHandler(column) {
        return column.sortable
            ? evt => {
                const key = getActualKey(evt);
                if (key === 'enter' || key === 'space') {
                    evt.preventDefault();
                    dispatchSort(column);
                }
            }
            : void 0;
    }

    /**
     * Create th element bindings for column
     *
     * @param {Object} column - column configuration object
     * @returns {Object} - th element bindings
     */
    function createSortableColumnBindings(column) {
        const bindings = {};

        if (column.sortable) {
            if (!column.sortOrder) {
                bindings['aria-sort'] = 'none';
            } else {
                bindings['aria-sort'] = column.sortOrder === 'asc' ? 'ascending' : 'descending';
            }
            bindings.tabindex = 0;
        }

        return bindings;
    }
</script>

<style>
    th {
        white-space: nowrap;
        vertical-align: top;
    }

    .sortable:hover {
        background-color: var(--color-gs-light-hover-bg);
        cursor: pointer;
    }

    .sortable {
        @add-mixin outline-focus 0;
    }

    .fitcontent {
        width: 1px;
    }
    .sortable:hover :global(.sort-indicator-arrow.inactive) {
        color: var(--color-gs-disabled-invert);
    }
</style>

<thead>
    <tr>
        {#each columns as column, columnIndex (typeof column.key === 'undefined' ? columnIndex : column.key)}
            {#if !column.title && !column.subTitle && !column.cellComponent && !column.sortable}
                <td
                    class:fitcontent={!!column.fitContent}
                    on:click={createColumnClickHandler(column)}
                    on:keydown|self={createColumnKeydownHandler(column)} />
            {:else}
                <th
                    scope="col"
                    on:click={createColumnClickHandler(column)}
                    on:keydown|self={createColumnKeydownHandler(column)}
                    class:fitcontent={!!column.fitContent}
                    class:sortable={column.sortable}
                    {...createSortableColumnBindings(column)}>
                    <TableHeadCell
                        title={column.title}
                        subTitle={column.subTitle}
                        cellComponent={column.cellComponent}
                        sortable={column.sortable}
                        sortOrder={column.sortOrder}
                        {allSelected}
                        {someSelected}
                        on:selectChange={evt => onSelectChange(columnIndex, column.key, evt.detail.checked)} />
                </th>
            {/if}
        {/each}
    </tr>
</thead>
